import { useCallback, useRef } from 'react'

interface UsePcmStreamingResult {
  setupPcmCapture: (
    streams: MediaStream[],
    useMultichannel: boolean,
    sendAudioChunk: (chunk: ArrayBuffer) => void,
  ) => Promise<void>
  cleanupPcm: () => Promise<void>
}

export function usePcmStreaming(): UsePcmStreamingResult {
  const pcmContextRef = useRef<AudioContext | null>(null)
  const pcmWorkletRef = useRef<AudioWorkletNode | null>(null)

  const setupMixedPcm = async (
    pcmContext: AudioContext,
    streams: MediaStream[],
    sendAudioChunk: (chunk: ArrayBuffer) => void,
  ) => {
    const workletNode = new AudioWorkletNode(pcmContext, 'pcm-processor')
    workletNode.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
      sendAudioChunk(event.data)
    }

    const destination = pcmContext.createMediaStreamDestination()
    for (const stream of streams) {
      const source = pcmContext.createMediaStreamSource(stream)
      source.connect(workletNode)
    }
    workletNode.connect(destination)
    pcmWorkletRef.current = workletNode
  }

  const setupMultichannelPcm = async (
    pcmContext: AudioContext,
    streams: MediaStream[],
    sendAudioChunk: (chunk: ArrayBuffer) => void,
  ) => {
    const destination = pcmContext.createMediaStreamDestination()

    for (let channelId = 0; channelId < streams.length; channelId++) {
      const stream = streams[channelId]
      const workletNode = new AudioWorkletNode(pcmContext, 'pcm-processor')

      const channel = channelId
      workletNode.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
        const pcmData = new Uint8Array(event.data)
        const taggedData = new Uint8Array(1 + pcmData.length)
        taggedData[0] = channel
        taggedData.set(pcmData, 1)
        sendAudioChunk(taggedData.buffer)
      }

      const source = pcmContext.createMediaStreamSource(stream)
      source.connect(workletNode)
      workletNode.connect(destination)
    }
  }

  const setupPcmCapture = useCallback(
    async (
      streams: MediaStream[],
      useMultichannel: boolean,
      sendAudioChunk: (chunk: ArrayBuffer) => void,
    ) => {
      try {
        const pcmContext = new AudioContext({ sampleRate: 24000 })
        await pcmContext.audioWorklet.addModule('/pcm-processor.js')

        if (useMultichannel) {
          await setupMultichannelPcm(pcmContext, streams, sendAudioChunk)
        } else {
          await setupMixedPcm(pcmContext, streams, sendAudioChunk)
        }

        pcmContextRef.current = pcmContext
      } catch (error) {
        console.error('Failed to setup PCM capture:', error)
      }
    },
    [],
  )

  const cleanupPcm = useCallback(async () => {
    if (pcmWorkletRef.current) {
      pcmWorkletRef.current.disconnect()
      pcmWorkletRef.current = null
    }
    if (pcmContextRef.current) {
      await pcmContextRef.current.close()
      pcmContextRef.current = null
    }
  }, [])

  return { setupPcmCapture, cleanupPcm }
}
